<template>
  <section class="create-merchant-sec">
    <h1 class="create-merchant-sec__title">Create Merchant Account</h1>
    <div class="create-merchant-sec__input-title">
      Business Information
    </div>
    <div class="create-merchant-sec__input-sub-title">
      Add your company info.
    </div>
    <b-form class="create-merchant-sec__form">
      <b-input-group
        class="create-merchant-sec__input-group--first"
        prepend="Legal Business name*">
        <b-input
          v-model.trim="legalBusinessName"
          type="text"
          placeholder="Peep Labs Inc"
          @blur.native="$v.legalBusinessName.$touch()"/>
      </b-input-group>
      <div
        v-if="validationErrors.legalBusinessName"
        class="create-merchant__error">{{ validationErrors.legalBusinessName }}</div>
      <b-input-group
        class="create-merchant-sec__input-group"
        prepend="Address*">
        <b-input
          v-model.trim="address"
          type="text"
          placeholder="185 Berry st."
          @blur.native="$v.address.$touch()"/>
      </b-input-group>
      <div
        v-if="validationErrors.addressLine"
        class="create-merchant__error">{{ validationErrors.addressLine }}</div>
      <b-input-group
        class="create-merchant-sec__input-group"
        prepend="City*">
        <b-input
          v-model.trim="city"
          type="text"
          placeholder="San Francisco"
          @blur.native="$v.city.$touch()"/>
      </b-input-group>
      <div
        v-if="validationErrors.addressCity"
        class="create-merchant__error">{{ validationErrors.addressCity }}</div>
      <b-input-group
        class="create-merchant-sec__input-group"
        prepend="Postal Code*">
        <b-input
          v-model.trim="postalCode"
          type="text"
          placeholder="94107"
          @blur.native="$v.postalCode.$touch()"/>
      </b-input-group>
      <div
        v-if="validationErrors.addressPostalCode"
        class="create-merchant__error">{{ validationErrors.addressPostalCode }}</div>
      <b-input-group
        v-if="countryHasState"
        class="create-merchant-sec__input-group state-select"
        prepend="State*">
        <b-select
          v-model="state"
          :options="stateList[country]"
          value-field="code"
          text-field="name"/>
      </b-input-group>
      <div
        v-if="validationErrors.addressState"
        class="create-merchant__error">{{ validationErrors.addressState }}</div>
      <b-input-group
        class="create-merchant-sec__input-group--last country-select"
        prepend="Country*">
        <b-select
          v-model="country"
          :options="countryList"
          value-field="code"
          text-field="name"/>
      </b-input-group>
      <div
        v-if="validationErrors.addressCountry"
        class="create-merchant__error">{{ validationErrors.addressCountry }}</div>
    </b-form>
    <div class="create-merchant-sec__notes">(*) indicates required fields.</div>
    <span
      v-if="errorMessage"
      class="error-message">
      {{ errorMessage }}
    </span>
    <b-button
      block
      class="create-merchant-sec__submit"
      @click="createAccount">Create Account</b-button>
    <b-button
      block
      class="create-merchant-sec__submit--back"
      @click="goBack">Back</b-button>
  </section>
</template>

<script>
import getGeoLocation from '@/helpers/getGeoLocation';
import setValidationErrors from '@/helpers/validationErrors';
import countryList from '@/helpers/lists/countries';
import stateList from '@/helpers/lists/states';

export default {
  name: 'CreateMerchantStep3',
  props: {
    lbnp: {
      type: String,
      default: '',
    },
    addressp: {
      type: Object,
      default() {
        return {
          line: '',
          city: '',
          postalCode: '',
          state: '',
          country: '',
        };
      },
    },
    creationError: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      legalBusinessName: '',
      address: '',
      city: '',
      postalCode: '',
      state: '',
      country: 'US',
      countryList,
      stateList,
      errorMessage: '',
      validationErrors: {},
    };
  },
  computed: {
    countryHasState() {
      return ['US', 'CA'].includes(this.country);
    },
    legalBusinessNameError() {
      if (!this.$v.legalBusinessName.required && this.$v.legalBusinessName.$dirty) {
        return 'Please enter legal business name';
      }
      return false;
    },
    addressError() {
      if (!this.$v.address.required && this.$v.address.$dirty) {
        return 'Please enter address';
      }
      return false;
    },
    cityError() {
      if (!this.$v.city.required && this.$v.city.$dirty) {
        return 'Please enter city';
      }
      return false;
    },
    postalCodeError() {
      if (!this.$v.postalCode.required && this.$v.postalCode.$dirty) {
        return 'Please enter postal code';
      }
      return false;
    },
    stateError() {
      if (!this.$v.state.required && this.$v.state.$dirty && this.countryHasState) {
        return 'Please enter state';
      }
      return false;
    },
    countryError() {
      if (!this.$v.country.required && this.$v.country.$dirty) {
        return 'Please enter country';
      }
      return false;
    },
  },
  watch: {
    country() {
      this.state = '';
    },
  },
  created() {
    if (this.lbnp.length) {
      this.legalBusinessName = this.lbnp;
    }
    if (!this.$lodash.isEmpty(this.addressp)) {
      this.address = this.addressp.line;
      this.city = this.addressp.city;
      this.postalCode = this.addressp.postal_code;
      this.state = this.addressp.state;
      this.country = this.addressp.country;
    }
  },
  mounted() {
    getGeoLocation()
      .then((response) => {
        this.country = response.country;
      })
      .catch(() => {
        this.country = 'US';
      });
  },
  methods: {
    goBack() {
      this.$emit('goBack');
    },
    createAccount() {
      const address = {
        line: this.address,
        city: this.city,
        postal_code: this.postalCode,
        state: this.state,
        country: this.country,
      };
      const payload = {
        legalBusinessName: this.legalBusinessName,
        address,
      };
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.$api('POST', '/validations/merchants/address', { address })
          .then((response) => {
            if (response.status === 200) {
              this.$emit('createAccount', payload);
            }
          })
          .catch((errors) => {
            this.validationErrors = setValidationErrors(errors.response.data.errors);
          });
      }
    },
  },
};
</script>
<style scoped>
  .country-select .custom-select, .state-select .custom-select {
    height: 44px !important;
    padding: 0 10px !important;
  }
</style>
